import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui-kit/button';
import { Input } from '@/components/ui-kit/input';
import { Label } from '@/components/ui-kit/label';
import { Skeleton } from '@/components/ui-kit/skeleton';
import { Building2 } from 'lucide-react';
import { useGetOrganizationById } from '@/api-client/use-get-orgnaization-by-id';
import {
  toUpdateOrganizationPayload,
  useUpdateOrganization,
} from '@/api-client/use-update-organization';
import { useCurrentOrgId, useMyOgnizationStore } from '@/store/use-my-oganization-store';
import type { StepOrgSettingsProps } from '../types/onboarding.types';
import { useForm } from 'react-hook-form';

export function StepOrgSettings({
  orgId,
  data,
  onChange,
  onComplete,
  onSkip,
  isLoading,
}: StepOrgSettingsProps) {
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { t } = useTranslation();
  const currentOrgId = useCurrentOrgId();
  const organizationId = orgId || currentOrgId;
  const { data: organization, isLoading: isOrgLoading } = useGetOrganizationById(organizationId);
  const { updateOrganizationAsync } = useUpdateOrganization();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: data.name,
    },
  });

  useEffect(() => {
    if (!organization?.itemId) return;
    reset({ name: organization.name ?? '' });
    onChange({ name: organization.name ?? '' });
    useMyOgnizationStore.getState().setCurrentOrganization({
      itemId: organization.itemId,
      name: organization.name ?? '',
    });
    // Populate once from the fetched org; onChange is a parent setter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization?.itemId, organization?.name, reset]);

  const onSubmit = handleSubmit(async (formData) => {
    if (!organization) {
      onComplete();
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      await updateOrganizationAsync(toUpdateOrganizationPayload(organization, formData.name));
      useMyOgnizationStore.getState().setCurrentOrganization({
        itemId: organization.itemId,
        name: formData.name,
      });
      onChange({ name: formData.name });
      onComplete();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('ONBOARDING_ERRORS_NETWORKERROR'));
    } finally {
      setIsSaving(false);
    }
  });

  if (isOrgLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="mx-auto h-16 w-16 rounded-full" />
        <Skeleton className="mx-auto h-6 w-48" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="space-y-6">
      <div className="text-center">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
          <Building2 className="h-8 w-8 text-primary" />
        </div>
        <h2 className="mt-4 text-xl font-semibold text-foreground">
          {t('ONBOARDING_STEPS_ORGSETTINGS_TITLE')}
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          {t('ONBOARDING_STEPS_ORGSETTINGS_DESCRIPTION')}
        </p>
      </div>

      {error && (
        <div className="rounded-md border border-destructive/20 bg-destructive/10 p-3 text-sm text-destructive">
          {error}
        </div>
      )}

      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="orgName">{t('ONBOARDING_FORM_ORGNAME')}</Label>
          <Input
            id="orgName"
            type="text"
            placeholder={t('ONBOARDING_FORM_ORGNAMEPLACEHOLDER')}
            {...register('name', {
              required: true,
              onChange: (e) => onChange({ name: e.target.value }),
            })}
          />
          {errors.name && (
            <p className="text-xs text-destructive">{t('ONBOARDING_FORM_ORGNAMEHELP')}</p>
          )}
          <p className="text-xs text-muted-foreground">{t('ONBOARDING_FORM_ORGNAMEHELP')}</p>
        </div>
      </div>

      <div className="flex justify-between pt-4">
        <Button type="button" variant="ghost" onClick={onSkip} disabled={isSaving || isLoading}>
          {t('ONBOARDING_BUTTONS_SKIP')}
        </Button>
        <Button
          type="submit"
          disabled={isSaving || isLoading || !organization}
          className="min-w-30"
        >
          {isSaving ? t('ONBOARDING_BUTTONS_SAVING') : t('ONBOARDING_BUTTONS_CONTINUE')}
        </Button>
      </div>
    </form>
  );
}
